<template>
  <TransitionGroup
    class="play-list"
    :css="animated"
    name="play-list-item"
    tag="div"
    @before-leave="freezeHeight"
  >
    <div
      v-for="entry in entries"
      :key="entry.play.key"
      :[EDITOR_PLAY_ITEM_ATTRIBUTE]="entry.play.id"
      class="play-list-item"
    >
      <div class="pb-100">
        <div
          class="play-list-slot"
          :style="entry.height === null ? undefined : { height: `${entry.height}px` }"
        >
          <Transition
            :duration="{ enter: 0, leave: EDITOR_PLAY_SLIDE_DURATION }"
            name="play-list-undo"
          >
            <EditorUndoCard
              v-if="entry.removalId"
              class="play-list-undo"
              @undo="handleUndo(entry.removalId)"
            />
          </Transition>

          <Transition name="play-list-card">
            <div
              v-if="!entry.removalId"
              :ref="(el) => setPlayElement(entry.play.id, el)"
              :[EDITOR_TUTORIAL_TARGET_ATTRIBUTE]="isSpotlit(entry.play.id) ? '' : undefined"
              :class="[
                'play-list-card',
                {
                  'play-list-card-glow': isHighlighted(entry.play.id),
                  'play-list-card-spotlight': spotlight === entry.play.id,
                  'play-list-card-tutorial-spotlight': isSpotlit(entry.play.id),
                },
              ]"
            >
              <LcSwipeable
                :ref="(el) => setSwipeable(entry.play.id, el)"
                :disabled="store.expandedPlayId === entry.play.id"
                :left-action="deleteAction"
                :right-action="quickPickAction"
                @swipe-end="handleSwipeEnd"
                @swipe-start="isSwiping = true"
                @trigger="handleTrigger(entry.play.id, $event)"
              >
                <slot :play="entry.play" />
              </LcSwipeable>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </TransitionGroup>

  <EditorTutorial />
</template>

<script generic="TPlay extends PlayLike & { id: string; key: string }" lang="ts" setup>
import type {
  LcSwipeableAction,
  LcSwipeableInstance,
  LcSwipeableSide,
} from '@lottocom/frontend-components'
import type { ComponentPublicInstance } from 'vue'

import { provideLcLotteryEditorCardGroup } from '@lottocom/frontend-components'

import type { Config, PlayLike, PlayListStore } from '../@types/editor'

import {
  EDITOR_PLAY_ITEM_ATTRIBUTE,
  EDITOR_PLAY_SLIDE_DURATION,
  EDITOR_SPOTLIGHT_Z_INDEX,
  EDITOR_UNDO_DURATION,
} from '../constants/editor'
import {
  EDITOR_TUTORIAL_SPOTLIGHT_Z_INDEX,
  EDITOR_TUTORIAL_START_DELAY_MS,
  EDITOR_TUTORIAL_TARGET_ATTRIBUTE,
} from '../constants/tutorial'
import { findTutorialPlayId, hasRoomForTutorial } from '../utils/editorTutorial'

type Entry = {
  height: Maybe<number>
  play: TPlay
  removalId: Maybe<string>
}

type Props = {
  animated?: boolean
  config: Config
  store: PlayListStore<TPlay>
}

type Slots = {
  default: (props: { play: TPlay }) => unknown
}

const { animated = true, config, store } = defineProps<Props>()

defineSlots<Slots>()

const { t } = useI18n()
const { cancel, isAnimating, isHighlighted, reset, setPlayVisible, triggerSinglePlay } =
  useEditorQuickPickAnimation()
const { followLeavingPlay, scrollToCenteredPlay, waitForScrollIdle } = useEditorScroll()
const { whenSettled } = useEditorSettling()
const tutorial = useEditorTutorial()

const spotlight = useEditorSpotlight()

provideLcLotteryEditorCardGroup()

const isSwiping = ref(false)

const playElements = new Map<string, HTMLElement>()
const swipeables = new Map<string, LcSwipeableInstance>()
const timers = new Map<string, ReturnType<typeof setTimeout>>()

let visibilityObserver: Maybe<IntersectionObserver> = null
let isUnmounted = false

const deleteAction = computed<LcSwipeableAction>(() => ({
  icon: 'trash-full',
  label: t('editor.playlist.swipe.action.delete'),
}))

const quickPickAction = computed<LcSwipeableAction>(() => ({
  icon: 'flash',
  label: t('editor.playlist.swipe.action.quickpick'),
}))

const entries = computed<Entry[]>(() => {
  const result: Entry[] = store.plays.map((play) => ({ height: null, play, removalId: null }))

  store.pendingRemovals.forEach(({ height, id, index, play }) => {
    result.splice(Math.min(index, result.length), 0, { height, play, removalId: id })
  })

  return result
})

const completePlayIds = computed(() =>
  entries.value
    .filter(({ play, removalId }) => !removalId && store.isPlayComplete(config, play))
    .map(({ play }) => play.id),
)

const readyPlayId = computed(() => {
  const [playId] = completePlayIds.value
  return playId && !isAnimating(playId) ? playId : null
})

const isSpotlit = (playId: string) =>
  tutorial.isActive.value && tutorial.targetPlayId.value === playId

const setSwipeable = (playId: string, instance: unknown) => {
  if (!instance) {
    swipeables.delete(playId)
    return
  }

  swipeables.set(playId, instance as LcSwipeableInstance)
}

const getPlayElement = (playId: string) => playElements.get(playId) ?? null

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const findTutorialTarget = () => findTutorialPlayId(completePlayIds.value, getPlayElement)

const toLayoutSignature = () => `${Math.round(window.scrollY)}:${store.plays.map(({ id }) => id)}`

const prepareTutorialTarget = async () => {
  await whenSettled()
  await waitForScrollIdle()
  await nextTick()

  const playId = findTutorialTarget()
  if (!playId || hasRoomForTutorial(getPlayElement(playId))) return playId

  await scrollToCenteredPlay(playId)
  await waitForScrollIdle()
  return playId
}

const startTutorial = async () => {
  if (tutorial.hasSeen.value) return

  store.setExpandedPlay(null)

  // until the overlay is up the customer can still add plays or scroll, moving the target out of view
  let playId: Maybe<string>
  let layout: string
  do {
    playId = await prepareTutorialTarget()
    if (!playId) return

    layout = toLayoutSignature()
    await pause(EDITOR_TUTORIAL_START_DELAY_MS)
    if (isUnmounted) return
  } while (layout !== toLayoutSignature() || playId !== findTutorialTarget())

  const targetId = playId
  await tutorial.start(targetId, () => swipeables.get(targetId) ?? null)
}

const handleClickCapture = (event: MouseEvent) => {
  if (!isSwiping.value) return
  event.preventDefault()
  event.stopPropagation()
}

const findPlayId = (element: Element) =>
  [...playElements.entries()].find(([, candidate]) => candidate === element)?.[0]

const setPlayElement = (playId: string, element: Maybe<ComponentPublicInstance | Element>) => {
  const previous = playElements.get(playId)
  previous?.removeEventListener('click', handleClickCapture, true)
  if (previous) visibilityObserver?.unobserve(previous)

  if (!(element instanceof HTMLElement)) {
    playElements.delete(playId)
    setPlayVisible(playId, false)
    return
  }

  playElements.set(playId, element)
  element.addEventListener('click', handleClickCapture, true)
  visibilityObserver?.observe(element)
}

const clearTimer = (removalId: string) => {
  const timer = timers.get(removalId)
  if (!timer) return
  clearTimeout(timer)
  timers.delete(removalId)
}

const handleDelete = (playId: string) => {
  cancel(playId)
  tutorial.abort(playId)

  const height = playElements.get(playId)?.offsetHeight || null
  const removalId = store.removePlay(playId, height)
  if (!removalId) return

  timers.set(
    removalId,
    setTimeout(() => {
      timers.delete(removalId)
      void followLeavingPlay(playId)
      store.expireRemoval(removalId, config)
    }, EDITOR_UNDO_DURATION),
  )
}

const handleTrigger = (playId: string, side: LcSwipeableSide) => {
  if (side === 'right') {
    const play = store.plays.find(({ id }) => id === playId)
    if (!play) return

    triggerSinglePlay({
      config,
      ignoredFields: store.getIgnoredFieldNames(config, play),
      kind: 'ball',
      play,
      quickPick: () => store.quickPick(playId, config),
      replaceAll: store.isPlayComplete(config, play),
    })
    return
  }

  handleDelete(playId)
}

const handleUndo = (removalId: Maybe<string>) => {
  if (!removalId) return
  clearTimer(removalId)
  store.restorePlay(removalId)
}

const handleSwipeEnd = () => {
  requestAnimationFrame(() => (isSwiping.value = false))
}

const freezeHeight = (element: Element) => {
  if (!(element instanceof HTMLElement)) return
  element.style.setProperty('--play-list-item-height', `${element.offsetHeight}px`)
}

watch(
  readyPlayId,
  (playId) => {
    if (!playId) return

    void startTutorial()
  },
  { immediate: true },
)

onMounted(() => {
  visibilityObserver = new IntersectionObserver((entries) =>
    entries.forEach((entry) => {
      const playId = findPlayId(entry.target)
      if (playId) setPlayVisible(playId, entry.isIntersecting)
    }),
  )
  playElements.forEach((element) => visibilityObserver?.observe(element))
})

onBeforeUnmount(() => {
  isUnmounted = true
  visibilityObserver?.disconnect()
  visibilityObserver = null
  tutorial.abort()
  reset()
  timers.forEach((timer) => clearTimeout(timer))
  timers.clear()
  store.pendingRemovals
    .map(({ id }) => id)
    .forEach((removalId) => store.expireRemoval(removalId, config))
})

// an import referenced only in style v-bind() is not exposed to the ssr render, a local binding is
const spotlightZIndex = EDITOR_SPOTLIGHT_Z_INDEX
const tutorialSpotlightZIndex = EDITOR_TUTORIAL_SPOTLIGHT_Z_INDEX
</script>

<style lang="scss" scoped>
$slide-duration: v-bind("EDITOR_PLAY_SLIDE_DURATION + 'ms'");
$spotlight-z-index: v-bind(spotlightZIndex);
$tutorial-spotlight-z-index: v-bind(tutorialSpotlightZIndex);
$glow-duration: 1100ms;
$shadow-glow:
  0px -1px 4px 0px #0008600f,
  0px 10px 16px 0px #0008600f,
  0px 8px 24px 0px #7fd5ff;

.play-list {
  margin-bottom: calc(-1 * #{token('size/space/gap/100')});
}

.play-list-slot {
  position: relative;
}

.play-list-undo {
  position: absolute;
  inset: 0;
}

.play-list-card {
  position: relative;
  z-index: 1;
}

.play-list-card-spotlight {
  z-index: $spotlight-z-index;
  pointer-events: none;
}

.play-list-card-tutorial-spotlight {
  z-index: $tutorial-spotlight-z-index;
  pointer-events: none;
}

.play-list-card-glow::after {
  content: '';
  position: absolute;
  inset: 0;
  border: token('size/border/100') solid token('color/blue/500');
  border-radius: token('size/radius/m');
  box-shadow: $shadow-glow;
  pointer-events: none;
  animation: play-list-card-glow $glow-duration ease-out both;
}

@keyframes play-list-card-glow {
  0%,
  100% {
    opacity: 0;
  }

  16%,
  62% {
    opacity: 1;
  }
}

.play-list-card-enter-active {
  transition: transform $slide-duration ease-out;
}

.play-list-card-enter-from {
  transform: translateX(-100%);
}

.play-list-card-leave-active {
  transition:
    transform $slide-duration ease-in,
    opacity $slide-duration ease-in;

  /* stylelint-disable selector-class-pattern */
  :deep(.lc-swipeable__action) {
    visibility: hidden;
  }

  :deep(.lc-swipeable__content) {
    transition: none;
  }
  /* stylelint-enable selector-class-pattern */
}

.play-list-card-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}

.play-list-undo-leave-active {
  pointer-events: none;
}

.play-list-item-leave-active {
  overflow: hidden;
  height: var(--play-list-item-height);
  pointer-events: none;
  transition:
    height $transition-duration-md $transition-ease-default,
    opacity $transition-duration-md ease-in;
}

.play-list-item-leave-to {
  height: 0;
  opacity: 0;
}

/* stylelint-disable-next-line selector-class-pattern */
.play-list-card :deep(.lc-swipeable__action--right) {
  background-color: token('color/background/accent/prominent/prominent');
  color: token('color/icon/primary/primary-inverted');

  /* stylelint-disable-next-line selector-class-pattern */
  &.lc-swipeable__action--past-threshold {
    background-color: token('color/background/accent/prominent/prominent-pressed');
  }
}
</style>
